import type { ProductBehavior } from '@up2stay/up2promo-api-client';
import {
  getLocationReadinessIssueForForm,
  partitionProductFormLocations,
  type LocationReadinessIssue,
  type ProductFormLocation,
} from '@up2stay/up2promo-commons/products';
import { Button } from '@up2stay/up2promo-ui';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  keyboardAwarePopoverCollision,
  useCssKeyboardInset,
} from '@/core/hooks/use-css-keyboard-inset';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';
import { useLocale } from '@/core/providers/dictionary-provider';

export type ProductLocationComboboxProps = {
  locations: ProductFormLocation[];
  value: string | null;
  onChange: (locationId: string | null) => void;
  knownProductType?: string | null;
  targetKindForReadiness?: ProductBehavior | string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  readyGroupLabel: string;
  incompleteGroupLabel: string;
  incompleteSuffix?: Partial<Record<LocationReadinessIssue, string>>;
  disabled?: boolean;
  /** Secondary create action in the list footer (when locations already exist). */
  onCreateLocation?: () => void;
  createLocationLabel?: string;
  /** Id of the visible label that names the trigger (DEV-540). */
  labelledBy?: string;
};

function locationLabel(loc: ProductFormLocation): string {
  return loc.name?.trim() || loc.id;
}

export function ProductLocationCombobox({
  locations,
  value,
  onChange,
  knownProductType,
  targetKindForReadiness,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  readyGroupLabel,
  incompleteGroupLabel,
  incompleteSuffix,
  disabled,
  onCreateLocation,
  createLocationLabel,
  labelledBy,
}: ProductLocationComboboxProps) {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const keyboardInset = useCssKeyboardInset(open);

  const selected = useMemo(
    () => locations.find((l) => l.id === value),
    [locations, value],
  );

  const { ready, incomplete } = useMemo(
    () =>
      partitionProductFormLocations(locations, {
        search,
        locale,
        knownProductType,
        targetKindForReadiness,
      }),
    [locations, search, locale, knownProductType, targetKindForReadiness],
  );

  const displayLabel = selected ? locationLabel(selected) : placeholder;

  const suffixForIssue = (issue: LocationReadinessIssue | null) => {
    if (!issue || !incompleteSuffix) return '';
    return incompleteSuffix[issue] ?? '';
  };

  const renderItem = (loc: ProductFormLocation, isIncomplete: boolean) => {
    const issue = isIncomplete
      ? getLocationReadinessIssueForForm(
          loc,
          knownProductType,
          targetKindForReadiness,
        )
      : null;
    const suffix = suffixForIssue(issue);
    const label = locationLabel(loc);
    const isSelected = loc.id === value;

    return (
      <CommandItem
        key={loc.id}
        value={`${loc.id}-${label}`}
        disabled={isIncomplete}
        className={cn(
          'cursor-pointer flex-col items-start gap-0.5',
          isIncomplete && 'opacity-60',
        )}
        onSelect={() => {
          if (isIncomplete) return;
          void triggerSelectionHaptic();
          onChange(loc.id);
          setOpen(false);
          setSearch('');
        }}
      >
        <div className="flex w-full min-w-0 items-center gap-2">
          <Check
            className={cn(
              'size-4 shrink-0',
              isSelected ? 'opacity-100' : 'opacity-0',
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate">
              {label}
              {suffix ? (
                <span className="text-muted-foreground">{` ${suffix}`}</span>
              ) : null}
            </span>
            {loc.address?.trim() ? (
              <span className="block truncate text-xs text-muted-foreground">
                {loc.address.trim()}
              </span>
            ) : null}
          </span>
        </div>
      </CommandItem>
    );
  };

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-labelledby={labelledBy}
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {displayLabel}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[var(--radix-popover-trigger-width)] max-w-[min(100vw-2rem,400px)] p-0"
        align="start"
        collisionPadding={keyboardAwarePopoverCollision(keyboardInset)}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[min(300px,calc(70dvh-var(--keyboard-inset-height,0px)))]">
            {ready.length === 0 && incomplete.length === 0 ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : null}
            {ready.length > 0 ? (
              <CommandGroup heading={readyGroupLabel}>
                {ready.map((loc) => renderItem(loc, false))}
              </CommandGroup>
            ) : null}
            {ready.length > 0 && incomplete.length > 0 ? (
              <CommandSeparator />
            ) : null}
            {incomplete.length > 0 ? (
              <CommandGroup heading={incompleteGroupLabel}>
                {incomplete.map((loc) => renderItem(loc, true))}
              </CommandGroup>
            ) : null}
            {onCreateLocation && createLocationLabel ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__create-location__"
                    className="cursor-pointer"
                    onSelect={() => {
                      void triggerSelectionHaptic();
                      setOpen(false);
                      setSearch('');
                      onCreateLocation();
                    }}
                  >
                    <Plus className="size-4 shrink-0" />
                    <span>{createLocationLabel}</span>
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
